import { useEffect, useState } from 'react';

import { api, ApiError, type SessionUser } from '../live/api.ts';
import { Spinner } from '../components/primitives.tsx';

/**
 * The front door.
 *
 * Deliberately informative: the most useful thing it can say before you are inside is whether
 * the system behind it is alive. It reports Nightshift health under the password box — an
 * operator who cannot get in should be able to tell "wrong password" from "the service is
 * down" without opening a terminal.
 */
export function Login({ onAuthed }: { onAuthed: (user: SessionUser) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const state = await api.login(username, password);
      if (!state.user) {
        setError('Sign-in failed.');
        return;
      }
      onAuthed(state.user);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 401
          ? 'That password is not right.'
          : caught instanceof Error
            ? caught.message
            : 'Sign-in failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = username.trim().length > 0 && password.length > 0;

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <div className="nav-mark" aria-hidden>
            N
          </div>
          <h1 className="login-title">Nightshift</h1>
        </div>

        <p style={{ color: 'var(--text-3)', lineHeight: 1.65 }}>
          Run and supervise your agent pipeline.
        </p>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="kicker">Username</span>
          <input
            className="input"
            type="text"
            autoFocus
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            aria-invalid={error !== null}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="kicker">Password</span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="input"
              type={reveal ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-invalid={error !== null}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setReveal((current) => !current)}
              aria-pressed={reveal}
              aria-label={reveal ? 'Hide password' : 'Show password'}
            >
              {reveal ? 'Hide' : 'Show'}
            </button>
          </span>
        </label>

        {error ? (
          <p style={{ color: 'var(--danger-text)', fontSize: 'var(--fs-small)' }} role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn btn-accent" disabled={busy || !canSubmit}>
          {busy ? <Spinner /> : null}
          {busy ? 'Signing in' : 'Sign in'}
        </button>

        <HealthLine />
      </form>
    </div>
  );
}

function HealthLine() {
  const [status, setStatus] = useState<'checking' | 'up' | 'down'>('checking');

  useEffect(() => {
    let live = true;
    void fetch('/healthz', { credentials: 'same-origin' })
      .then((response) => {
        if (live) setStatus(response.ok ? 'up' : 'down');
      })
      .catch(() => {
        if (live) setStatus('down');
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="conn" style={{ fontSize: 'var(--fs-tiny)' }}>
      <span
        className={`dot${status === 'up' ? ' breathe' : ''}`}
        style={{
          background:
            status === 'up' ? 'var(--good)' : status === 'down' ? 'var(--danger)' : 'var(--text-4)',
        }}
      />
      {status === 'up'
        ? 'Nightshift is up'
        : status === 'down'
          ? 'Nightshift is not answering — signing in will not help until it is back'
          : 'checking Nightshift'}
    </div>
  );
}
